import { useEffect, useRef, useState } from "react";
import { useUrlState } from "@/lib/useUrlState";
import Breadcrumbs from "@/components/Breadcrumbs";
import {
  Plus, Loader2, FileText, Table2, Info, Send, CheckCircle2, XCircle, Ban,
  Trash2, CopyPlus, Minus,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import EstimateTab from "./EstimateTab";
import ProposalTab from "./ProposalTab";
import { estApi } from "../lib/api";

const money = (v) => {
  const n = Number(v || 0);
  const body = `$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  return n < 0 ? `-${body}` : body;
};
const STATUS_STYLES = {
  created: "bg-slate-100 text-slate-600",
  submitted: "bg-blue-100 text-blue-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  void: "bg-slate-200 text-slate-500 line-through",
};
const CO_TABS = [
  { key: "details", label: "Details", icon: Info },
  { key: "pricing", label: "Pricing", icon: Table2 },
  { key: "proposal", label: "Proposal", icon: FileText },
];

function StatusChip({ status }) {
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
      STATUS_STYLES[status] || "bg-slate-100 text-slate-600"}`}>
      {status}
    </span>
  );
}

function ChangeOrderDetail({ coId, onBack, onChanged, reasons = [] }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("details");
  const [confirm, setConfirm] = useState(null);
  const [approver, setApprover] = useState("");

  const load = () => estApi.getChangeOrder(coId).then(setData);
  useEffect(() => { load(); // eslint-disable-next-line
  }, [coId]);

  const save = (patch) => estApi.updateChangeOrder(coId, patch).then(setData)
    .catch((e) => toast.error(e?.response?.data?.detail || "Could not save"));

  const setStatus = async (status) => {
    try {
      const res = await estApi.setCoStatus(coId, {
        status, approved_by: status === "approved" ? approver : "",
      });
      setData(res);
      setConfirm(null);
      onChanged?.();
      toast.success(`Change order ${status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status change refused");
    }
  };

  if (!data) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-slate-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading change order…
      </div>
    );
  }
  const co = data.change_order;

  return (
    <div data-testid="co-detail">
      <div className="flex items-center gap-3">
        <Breadcrumbs trail={[{ label: "Change orders", dropParam: "co", testId: "co-back-btn" }]} />
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-bold text-slate-900">
              CO-{String(co.co_number).padStart(3, "0")} · {co.co_title}
            </h3>
            <StatusChip status={co.co_status} />
          </div>
          <p className="text-[11px] text-slate-500">
            {data.parent.project_name} · {data.parent.estimate_number} · this CO{" "}
            {money(co.total_sales)} · revised contract{" "}
            {money(data.summary.current_contract_value)}
          </p>
        </div>
        <div className="ml-auto flex gap-1.5">
          {co.co_status === "created" && (
            <Button size="sm" className="text-xs" onClick={() => setConfirm("submitted")}
                    data-testid="co-submit-btn">
              <Send className="mr-1.5 h-3.5 w-3.5" /> Submit
            </Button>
          )}
          {co.co_status === "submitted" && (
            <>
              <Button size="sm" className="text-xs" onClick={() => setConfirm("approved")}
                      data-testid="co-approve-btn">
                <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Approve
              </Button>
              <Button size="sm" variant="outline" className="text-xs"
                      onClick={() => setConfirm("rejected")} data-testid="co-reject-btn">
                <XCircle className="mr-1.5 h-3.5 w-3.5" /> Reject
              </Button>
            </>
          )}
          {co.co_status !== "void" && (
            <Button size="sm" variant="ghost" className="text-xs text-slate-500"
                    onClick={() => setConfirm("void")} data-testid="co-void-btn">
              <Ban className="mr-1.5 h-3.5 w-3.5" /> Void
            </Button>
          )}
        </div>
      </div>

      <div className="mt-3 flex gap-1 border-b border-slate-200">
        {CO_TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium ${
                    tab === t.key ? "border-[#007BFF] text-[#007BFF]"
                      : "border-transparent text-slate-500 hover:text-slate-800"}`}
                  data-testid={`co-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {tab === "details" && (
          <div className="max-w-2xl space-y-3" data-testid="co-details-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-slate-500">Title (max 30)</label>
                <Input defaultValue={co.co_title} maxLength={30} className="h-8 text-xs"
                       onBlur={(e) => save({ co_title: e.target.value })}
                       data-testid="co-title-input" />
              </div>
              <div>
                <label className="text-[11px] text-slate-500">GC reference</label>
                <Input defaultValue={co.gc_reference} className="h-8 text-xs"
                       onBlur={(e) => save({ gc_reference: e.target.value })}
                       data-testid="co-gc-ref-input" />
              </div>
              <div>
                <label className="text-[11px] text-slate-500">Change reason</label>
                <select defaultValue={co.change_reason}
                        onChange={(e) => save({ change_reason: e.target.value })}
                        className="h-8 w-full rounded border border-slate-200 bg-white px-2 text-xs"
                        data-testid="co-reason-select">
                  {(reasons.length ? reasons : ["client_request", "design_development",
                    "allowance", "existing_condition", "back_charge", "other"]).map((r) => (
                    <option key={r} value={r}>{r.replace(/_/g, " ")}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] text-slate-500">Schedule impact (days)</label>
                <Input type="number" defaultValue={co.schedule_impact_days ?? ""}
                       className="h-8 text-xs"
                       onBlur={(e) => save({
                         schedule_impact_days: e.target.value === "" ? null
                           : parseInt(e.target.value, 10) })}
                       data-testid="co-schedule-input" />
              </div>
            </div>
            <div>
              <label className="text-[11px] text-slate-500">Description</label>
              <textarea defaultValue={co.description} rows={4}
                        onBlur={(e) => save({ description: e.target.value })}
                        className="w-full rounded border border-slate-200 px-2 py-1.5 text-xs leading-relaxed focus:border-[#007BFF] focus:outline-none"
                        data-testid="co-description-input" />
            </div>
            <div>
              <label className="text-[11px] text-slate-500">Internal notes</label>
              <textarea defaultValue={co.internal_notes} rows={3}
                        onBlur={(e) => save({ internal_notes: e.target.value })}
                        className="w-full rounded border border-slate-200 px-2 py-1.5 text-xs leading-relaxed focus:border-[#007BFF] focus:outline-none"
                        data-testid="co-notes-input" />
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Status history
              </h4>
              {data.status_log.map((l) => (
                <div key={l.id} className="flex items-center gap-2 text-[11px] text-slate-600">
                  <StatusChip status={l.to_status} />
                  <span className="text-slate-400">
                    {l.from_status ? `from ${l.from_status} · ` : ""}{l.user} ·{" "}
                    {new Date(l.created_at).toLocaleString()}
                  </span>
                  {l.note && <span className="text-slate-500">— {l.note}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === "pricing" && (
          <CoPricing co={co} parentId={data.parent.id} readOnly={data.locked}
                     onChanged={() => { load(); onChanged?.(); }} />
        )}
        {tab === "proposal" && <ProposalTab estimate={co} />}
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent data-testid="co-status-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark this change order {confirm}?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "submitted" && "It needs at least one line, no open vendor quotes and a generated proposal PDF."}
              {confirm === "approved" && "Approving locks the lines and adds this CO to the contract value."}
              {confirm === "rejected" && "The record is kept but excluded from the contract value."}
              {confirm === "void" && "Voiding is permanent — the record stays for the audit trail."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirm === "approved" && (
            <Input placeholder="Approved by (name)" value={approver}
                   onChange={(e) => setApprover(e.target.value)}
                   className="h-8 text-xs" data-testid="co-approver-input" />
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="co-status-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => setStatus(confirm)} data-testid="co-status-ok">
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CoPricing({ co, parentId, onChanged, readOnly = false }) {
  const [copyOpen, setCopyOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [parentLines, setParentLines] = useState([]);
  const [picked, setPicked] = useState([]);
  const [sign, setSign] = useState("add");
  const [totals, setTotals] = useState(null);

  const openCopy = async () => {
    const res = await estApi.getLines(parentId);
    setParentLines(res.rollup.lines);
    setPicked([]);
    setCopyOpen(true);
  };

  const refreshTotals = () => estApi.getAddsDeducts(co.id).then(setTotals).catch(() => {});
  useEffect(() => { refreshTotals(); // eslint-disable-next-line
  }, [co.id]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" className="text-xs" onClick={openCopy}
                disabled={readOnly} data-testid="co-copy-lines-btn">
          <CopyPlus className="mr-1.5 h-3.5 w-3.5" /> Copy from base estimate
        </Button>
        {totals && (
          <span className="ml-auto text-xs text-slate-600" data-testid="co-adds-deducts">
            adds {money(totals.adds)} · deducts{" "}
            <span className="text-red-600">{money(totals.deducts)}</span> · net{" "}
            <strong>{money(totals.net)}</strong>
          </span>
        )}
      </div>

      <EstimateTab estimate={co} reloadKey={reloadKey} readOnly={readOnly}
                   onTotals={() => { refreshTotals(); onChanged?.(); }} />

      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent className="max-w-3xl" data-testid="co-copy-dialog">
          <DialogHeader><DialogTitle>Copy lines from the base estimate</DialogTitle></DialogHeader>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-500">Add as</span>
            <select value={sign} onChange={(e) => setSign(e.target.value)}
                    className="h-7 rounded border border-slate-200 px-1 text-xs"
                    data-testid="co-copy-sign">
              <option value="add">additions</option>
              <option value="deduct">deducts (negative)</option>
            </select>
          </div>
          <div className="max-h-72 overflow-auto rounded border border-slate-200">
            <table className="w-full text-xs">
              <tbody>
                {parentLines.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100"
                      data-testid={`co-copy-row-${l.id}`}>
                    <td className="w-8 px-2 py-1.5">
                      <input type="checkbox" checked={picked.includes(l.id)}
                             onChange={() => setPicked((p) => p.includes(l.id)
                               ? p.filter((x) => x !== l.id) : [...p, l.id])} />
                    </td>
                    <td className="px-2 py-1.5 text-slate-800">{l.description}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{l.qty}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{money(l.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyOpen(false)}>Cancel</Button>
            <Button disabled={picked.length === 0}
                    onClick={async () => {
                      await estApi.copyCoLines(co.id, { line_ids: picked, sign });
                      setCopyOpen(false);
                      setReloadKey((k) => k + 1);
                      refreshTotals();
                      onChanged?.();
                      toast.success(`${picked.length} line(s) copied as ${sign}`);
                    }}
                    data-testid="co-copy-submit">
              {sign === "deduct" ? <Minus className="mr-1.5 h-3.5 w-3.5" />
                                 : <Plus className="mr-1.5 h-3.5 w-3.5" />}
              Copy {picked.length} line(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ChangeOrdersTab({ estimate, onRefresh }) {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useUrlState("co", "");
  const wasOpen = useRef(false);
  useEffect(() => { if (wasOpen.current && !openId) { load(); onRefresh?.(); } wasOpen.current = !!openId; }, [openId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ co_title: "", description: "", change_reason: "client_request",
    gc_reference: "" });

  const load = () => estApi.listChangeOrders(estimate.id).then(setData);
  useEffect(() => { load().catch(() => toast.error("Could not load change orders")); // eslint-disable-next-line
  }, [estimate.id]);

  if (openId) {
    return (
      <ChangeOrderDetail coId={openId} reasons={data?.reasons || []}
                         onBack={() => setOpenId("")}
                         onChanged={() => { load(); onRefresh?.(); }} />
    );
  }
  if (!data) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-slate-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading change orders…
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="change-orders-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">
          Change orders ({data.change_orders.length})
        </h3>
        <Button size="sm" className="text-xs" onClick={() => setNewOpen(true)}
                data-testid="new-co-btn">
          <Plus className="mr-1.5 h-3.5 w-3.5" /> New change order
        </Button>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-xs" data-testid="co-table">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              {["CO #", "Title", "Reason", "Status", "Total", "GM%", "Submitted", "Approved",
                ""].map((h) => (
                <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.change_orders.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-slate-400">
                No change orders yet.
              </td></tr>
            )}
            {data.change_orders.map((co) => (
              <tr key={co.id} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                  onClick={() => setOpenId(co.id)} data-testid={`co-row-${co.id}`}>
                <td className="px-3 py-2 font-mono text-slate-600">
                  CO-{String(co.co_number).padStart(3, "0")}
                </td>
                <td className="px-3 py-2 text-slate-800">{co.co_title}</td>
                <td className="px-3 py-2 text-slate-500">
                  {(co.change_reason || "").replace(/_/g, " ")}
                </td>
                <td className="px-3 py-2"><StatusChip status={co.co_status} /></td>
                <td className={`px-3 py-2 tabular-nums ${
                  (co.total_sales || 0) < 0 ? "text-red-600" : "text-slate-800"}`}>
                  {money(co.total_sales)}
                </td>
                <td className="px-3 py-2 tabular-nums text-slate-500">
                  {((co.total_gm_percent || 0) * 100).toFixed(1)}%
                </td>
                <td className="px-3 py-2 text-slate-400">
                  {co.submitted_at ? new Date(co.submitted_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-3 py-2 text-slate-400">
                  {co.approved_at ? new Date(co.approved_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-2 py-2">
                  {["created", "rejected", "void"].includes(co.co_status) && (
                    <button className="text-slate-400 hover:text-red-600"
                            onClick={async (e) => {
                              e.stopPropagation();
                              await estApi.deleteChangeOrder(co.id);
                              toast.success("Change order deleted");
                              load();
                              onRefresh?.();
                            }}
                            data-testid={`co-delete-${co.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-lg" data-testid="new-co-dialog">
          <DialogHeader><DialogTitle>New change order</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div>
              <label className="flex items-center justify-between text-[11px] text-slate-500">
                <span>Title</span>
                <span className={form.co_title.length >= 30 ? "text-amber-600" : "text-slate-400"}
                      data-testid="new-co-title-counter">
                  {form.co_title.length}/30
                </span>
              </label>
              <Input value={form.co_title} maxLength={30} className="h-8 text-xs"
                     onChange={(e) => setForm({ ...form, co_title: e.target.value })}
                     data-testid="new-co-title" />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-[11px] text-slate-500">Reason</label>
                <select value={form.change_reason}
                        onChange={(e) => setForm({ ...form, change_reason: e.target.value })}
                        className="h-8 w-full rounded border border-slate-200 bg-white px-2 text-xs"
                        data-testid="new-co-reason">
                  {data.reasons.map((r) => (
                    <option key={r} value={r}>{r.replace(/_/g, " ")}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="text-[11px] text-slate-500">GC reference</label>
                <Input value={form.gc_reference} className="h-8 text-xs"
                       onChange={(e) => setForm({ ...form, gc_reference: e.target.value })}
                       data-testid="new-co-gc-ref" />
              </div>
            </div>
            <div>
              <label className="text-[11px] text-slate-500">Description</label>
              <textarea value={form.description} rows={3}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        className="w-full rounded border border-slate-200 px-2 py-1.5 text-xs focus:border-[#007BFF] focus:outline-none"
                        data-testid="new-co-description" />
            </div>
            <p className="text-[10px] text-slate-400">
              Rates, margins, tax and Textura are copied from the base estimate and can be changed
              per change order.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button onClick={async () => {
              try {
                const co = await estApi.createChangeOrder(estimate.id, form);
                setNewOpen(false);
                setForm({ co_title: "", description: "", change_reason: "client_request",
                  gc_reference: "" });
                await load();
                onRefresh?.();
                setOpenId(co.id);
                toast.success(`CO-${String(co.co_number).padStart(3, "0")} created`);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not create it");
              }
            }} data-testid="new-co-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
